import { AsModuleHelper, AsStoreModule } from '@/types/vuex/helpers';
import { createNamespacedHelpers, ActionContext, MutationTree, Dictionary } from 'vuex';
import { Theme } from '@/types/theme';
import { IRootState } from '@/store/rootstate';
import { IBankItem, IBankSearchRequest, IPagedResult, ISignupField, IValidisConfigDetailsResponse, IScenarioMessageResponse } from '@/api/signup/interface';

interface IState {
  bank?: IBankItem;
  bankSearch?: string;
  info: Partial<ISignupField>;
  static: Partial<ISignupField>;
  validisConfig: Partial<IValidisConfigDetailsResponse>;
  acceptedTerms: boolean;
  acceptedPrivacy: boolean;
  externalReference: string; 
  referenceGuid: string;
  collections?: object;
  scenarioMessage: Partial<IScenarioMessageResponse>;
  collectionsEmployment?: object;
  collectionsBau?: object;
  collectionsBauPriority?: object;
  collectionsBus?: object;
  collectionsBusEmployment?: object;
  householdComposition?: object;
  financialCircumstances?: object;
  income?: object;
  monthlyExpenditure?: object;
}

interface IGetters {
  info: ISignupField;
  validisConfig: IValidisConfigDetailsResponse;
  bank?: IBankItem;
  bankSearch?: string;
  acceptedTerms: boolean;
  acceptedPrivacy: boolean;
  externalReference: string; 
  referenceGuid: string;
  scenarioMessage: IScenarioMessageResponse;
}

export interface IUserInit {
  bank?: IBankItem;
  bankSearch?: string;
  static: Partial<ISignupField>;
  acceptedTerms: boolean;
  acceptedPrivacy: boolean;
  externalReference: string; 
  referenceGuid?: string;
}

interface IActions {
  init(payload: IUserInit): Promise<void>;
  isStatic(name: keyof ISignupField): Promise<boolean>;

  updateInfo(about: Partial<ISignupField>): void;
  setValidisConfig(response: Partial<IValidisConfigDetailsResponse>): void;
  setScenarioMessage(response: Partial<IScenarioMessageResponse>): void;
  clearBank(): void;
}

interface IStoreActions<S, R> {
  init(injectee: ActionContext<S, R>, payload: IUserInit): void;
  isStatic(injectee: ActionContext<S, R>, name: keyof ISignupField): boolean;
  updateInfo(injectee: ActionContext<S, R>, about: Partial<ISignupField>): void;
  setValidisConfig(injectee: ActionContext<S, R>, about: Partial<IValidisConfigDetailsResponse>): void;
  clearBank(injectee: ActionContext<S, R>): void;
  setScenarioMessage(injectee: ActionContext<S, R>, payload: Partial<IScenarioMessageResponse>): void;
}

export enum Mutations {
  ASSIGN_STATIC = 'ASSIGN_STATIC',
  SET_BANK = 'SET_BANK_ID',
  SET_VALIDIS_CONFIG = 'SET_VALIDIS_CONFIG',
  SET_BANK_SEARCH = 'SET_BANK_SEARCH',
  SET_EXTERNAL_REFERENCE = 'SET_EXTERNAL_REFERENCE',
  SET_REFERENCE_GUID = 'SET_REFERENCE_GUID',
  UPDATE_INFO = 'UPDATE_INFO',
  UPDATE_PRIVACY_TERMS = 'UPDATE_PRIVACY_TERMS',
  UPDATE_COLLECTIONS = 'UPDATE_COLLECTIONS',
  SET_SCENARIO_MESSAGE = 'SET_SCENARIO_MESSAGE',
  UPDATE_COLLECTIONS_EMPLOYMENT = 'UPDATE_COLLECTIONS_EMPLOYMENT',
  UPDATE_COLLECTIONS_BAU = 'UPDATE_COLLECTIONS_BAU',
  UPDATE_COLLECTIONS_BAU_PRIORITY = 'UPDATE_COLLECTIONS_BAU_PRIORITY',
  UPDATE_COLLECTIONS_BUS = 'UPDATE_COLLECTIONS_BUS',
  UPDATE_COLLECTIONS_BUS_EMPLOYMENT = 'UPDATE_COLLECTIONS_BUS_EMPLOYMENT',
  UPDATE_COLLECTIONS_BUS_RFD = 'UPDATE_COLLECTIONS_BUS_RFD',
  UPDATE_HOUSEHOLD_COMPOSITION = 'UPDATE_HOUSEHOLD_COMPOSITION',
  UPDATE_FINANCIAL_CIRCUMSTANCES = 'UPDATE_FINANCIAL_CIRCUMSTANCES',
  UPDATE_INCOME = 'UPDATE_INCOME',
  UPDATE_MONTHLY_EXPENDITURE = 'UPDATE_MONTHLY_EXPENDITURE',
}

type MutationType<S, E, K = keyof keyof E> = { [k in keyof K]: MutationTree<S> };

export type module = AsStoreModule<
  IState,
  IGetters,
  IStoreActions<IState, IRootState>,
  MutationType<IState, Mutations>
>;
export default createNamespacedHelpers('user') as AsModuleHelper<IState, IGetters, IActions>;
